import HeaderBrand from "@/shared/layout/Header/HeaderBrand";
import React, { useState } from "react";

import { getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { useSecureAccess } from "@/shared/permissionBase";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

export default function AuthSidebar(): React.JSX.Element {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  // Only modules the backend grants the "view" action for show up here.
  const { navItems: filteredNavItems } = useSecureAccess();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const sidebarClasses = getThemeComponentGroup("sidebar");
  const sidebarBgClass = sidebarClasses?.bgColor || "bg-theme-surface-subtle";
  const sidebarTextClass = sidebarClasses?.textColor || "text-theme-text";
  const activeItemBgClass = sidebarClasses?.activeItemBgColor || "bg-theme-primary";
  const activeItemTextClass = sidebarClasses?.activeItemTextColor || "text-theme-text-inverse";
  const inactiveItemTextClass = sidebarClasses?.inactiveItemTextColor || "text-theme-text-muted";
  const toggleButtonBgClass =
    sidebarClasses?.toggleButtonBgColor ||
    "border border-theme-border bg-theme-surface-subtle hover:bg-theme-surface-muted";
  const toggleIconClass = sidebarClasses?.toggleIconColor || "text-theme-text";

  const getActiveTab = () => {
    const currentPath = pathname.split("?")[0];

    const activeItem = filteredNavItems.find(
      (item) => currentPath === item.path || currentPath.startsWith(`${item.path}/`)
    );

    return activeItem?.id ?? "";
  };

  const callOnTabChange = (item) => {
    navigate(item.path);
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <aside
      className={`
         relative inset-y-0 left-0 z-40 transform transition-all duration-300 ease-in-out overflow-hidden
        md:translate-x-0 border-r border-theme-border/70 flex flex-col shadow-sm
        ${sidebarBgClass} ${sidebarTextClass} ${sidebarClasses?.root || ""}
        ${isCollapsed ? "w-16" : "w-65"}
      `}
    >
      <div className="pointer-events-none absolute inset-0 bg-linear-to-b from-theme-primary/5 via-transparent to-theme-accent/10" />
      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto thin-scrollbar">
        {/* Branch Header */}
        <div className={`mb-4 mt-5 ${isCollapsed ? "mx-1 flex justify-center" : "mx-6"}`}>
          {!isCollapsed && <HeaderBrand variant="sidebar" />}
          {isCollapsed && (
            <div className="flex justify-center transform scale-50 origin-center">
              <HeaderBrand variant="sidebar" />
            </div>
          )}
        </div>

        <nav className="flex-1 pt-0">
          {filteredNavItems?.map((item) => {
            const isActive = getActiveTab() === item.id;

            return (
              <button
                key={item.id}
                id={`sidebar-item-${item.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => callOnTabChange(item)}
                title={isCollapsed ? item.label : undefined}
                className={`group relative flex w-full items-center gap-3 py-3.5 text-left transition-colors duration-200
                  ${isCollapsed ? "justify-center px-2" : "px-5"}
                  ${
                    isActive
                      ? `${activeItemBgClass} font-bold ${activeItemTextClass} shadow-sm ${sidebarClasses?.activeItem || ""}`
                      : `${inactiveItemTextClass} hover:bg-theme-primary/5 hover:text-theme-primary hover:shadow-[inset_3px_0_0_rgba(0,0,0,0.08)] ${sidebarClasses?.inactiveItem || ""}`
                  }
                `}
              >
                {isActive && (
                  <span className="absolute inset-y-1 right-0 w-1.5 rounded-l-full bg-theme-accent" />
                )}
                {!isActive && (
                  <span className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-theme-primary opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                )}
                <span
                  className={`flex shrink-0 items-center justify-center transition-colors duration-200 ${
                    isActive
                      ? "text-current"
                      : "text-theme-text-soft group-hover:text-theme-primary"
                  }`}
                >
                  <item.icon size={20} strokeWidth={isActive ? 2.8 : 2.2} />
                </span>
                {!isCollapsed && (
                  <span className="min-w-0 flex-1 text-sm font-semibold">{item.label}</span>
                )}
                {!isCollapsed && (
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full transition-all duration-200 ${
                      isActive
                        ? "bg-theme-accent opacity-100"
                        : "bg-theme-primary opacity-0 group-hover:opacity-100"
                    }`}
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Collapsible Button */}
        <div className={`py-4 ${isCollapsed ? "flex justify-center" : "flex justify-end pr-4"}`}>
          <button
            id="admin-sidebar-toggle"
            onClick={toggleSidebar}
            className={`flex h-8 w-8 items-center justify-center rounded-full text-theme-text transition-colors duration-200 ${toggleButtonBgClass} ${sidebarClasses?.toggleButton || ""}`}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight size={20} className={toggleIconClass} />
            ) : (
              <ChevronLeft size={20} className={toggleIconClass} />
            )}
          </button>
        </div>
      </div>
    </aside>
  );
}
